import { useEffect, useState } from 'react';
import { Modal, Backdrop, Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Close';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useTextStyles } from 'src/hooks/useTextStyles';
import Button from 'src/components/NewButton';
import TextInput from 'src/components/TextInput';
import { ReactComponent as CampaignsIcon } from 'src/assets/icons/campaigns.svg';
import { updateCampaign } from 'src/apis/campaign';
import useNotification from 'src/hooks/useNotification';
import { CampaignProps } from 'src/types/campaign';

interface EditCampaignModalProps {
  open: boolean;
  campaign: CampaignProps | null;
  closeModal: () => void;
  onUpdated: (campaign: CampaignProps) => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    logo: {
      width: 50,
      height: 50,
      padding: 12,
      borderRadius: 8,
      border: '1px solid var(--Gray-200, #EAECF0)',
      background: '#FFF',
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      marginTop: 20,
    },
    inputField: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    },
    input: {
      padding: '6px 8px',
      borderRadius: 8,
      border: '1px solid #C3C3C7',
      width: '100%',
      '& .MuiInputBase-root:before, & .MuiInputBase-root:after': {
        border: 'none !important',
      },
    },
    buttons: {
      paddingTop: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      '& button': {
        flex: 1,
      },
    },
  })
);

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 480,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
};

export const EditCampaignModal = ({
  open,
  campaign,
  closeModal,
  onUpdated,
}: EditCampaignModalProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const { handleError, handleSuccess } = useNotification();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (open && campaign) {
      setName(campaign.name || '');
      setDescription(campaign.description || '');
      setHasError(false);
      setLoading(false);
    }
  }, [open, campaign]);

  const handleSave = async () => {
    if (!campaign) {
      return;
    }
    if (!name.trim()) {
      setHasError(true);
      return;
    }

    setLoading(true);
    try {
      const updated = await updateCampaign(campaign.id, {
        name: name.trim(),
        description: description.trim(),
      });
      handleSuccess('Campaign updated successfully');
      onUpdated({
        ...campaign,
        ...updated,
        name: name.trim(),
        description: description.trim(),
        id: campaign.id,
      });
      closeModal();
    } catch (error) {
      handleError(error, 'There was an error while updating the campaign');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={closeModal}
      aria-labelledby='edit-campaign-modal-title'
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 800,
      }}
    >
      <Box sx={{ ...modalStyle }}>
        <Box className={classes.header}>
          <Box className={classes.logo}>
            <CampaignsIcon
              fill='none'
              stroke='#344054'
              strokeWidth={1}
              strokeLinecap='round'
              strokeLinejoin='round'
              style={{ width: 24, height: 24 }}
            />
          </Box>
          <CancelIcon
            cursor='pointer'
            width={24}
            height={24}
            onClick={closeModal}
          />
        </Box>
        <Typography className={textClasses.lgBold} style={{ color: '#101828' }}>
          Edit Campaign
        </Typography>
        <Box className={classes.content}>
          <Box className={classes.inputField}>
            <Typography variant='body1' style={{ fontSize: 14 }}>
              Campaign Name
            </Typography>
            <TextInput
              style={
                hasError && !name.trim()
                  ? { border: '1px solid #B42318' }
                  : undefined
              }
              default='Enter name'
              value={name}
              onChange={setName}
              className={classes.input}
            />
          </Box>
          <Box className={classes.inputField}>
            <Typography variant='body1' style={{ fontSize: 14 }}>
              Description
            </Typography>
            <TextInput
              default='Enter description'
              value={description}
              onChange={setDescription}
              className={classes.input}
            />
          </Box>
        </Box>
        <Box className={classes.buttons}>
          <Button
            title='Cancel'
            variant='outlined'
            height={46}
            onClick={closeModal}
            disabled={loading}
          />
          <Button height={46} onClick={handleSave} disabled={loading}>
            {loading ? (
              <CircularProgress style={{ color: '#fff' }} size={24} />
            ) : (
              <span>Save</span>
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
